import React, { useState, useEffect } from "react";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { useTranslation } from "react-i18next";
import { IoMdCheckmark } from "react-icons/io";
import { ApiGet } from "../helper/axiosFunctions";
import { commonToast } from "../helper/Toast";

// SubjectFilter component with API integration

export default function SubjectFilter({ onSubjectChange }) {
    const { t } = useTranslation();
    const [subjects, setSubjects] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedSubject, setSelectedSubject] = useState("All Subjects");
    const [displayText, setDisplayText] = useState("By Subjects");

    useEffect(() => {
        fetchSubjects();
    }, []);

    const fetchSubjects = async () => {
        setLoading(true);
        try {
            const response = await ApiGet('/contents/decks/get-filters');

            // Handle different possible response formats
            let subjectsList = [];
            if (response?.data?.subjects) {
                subjectsList = response.data.subjects;
            } else if (response?.subjects) {
                subjectsList = response.subjects;
            } else if (response?.data && Array.isArray(response.data)) {
                subjectsList = response.data;
            } else if (Array.isArray(response)) {
                subjectsList = response;
            }

            // Ensure we have an array of strings and filter out empty values
            if (Array.isArray(subjectsList)) {
                const filteredSubjects = subjectsList
                    .filter(subject => typeof subject === 'string' && subject.trim())
                    .sort();
                setSubjects(filteredSubjects);
            } else {
                setSubjects([]);
            }

        } catch (error) {
            commonToast("Failed to load subjects", "error");
            setSubjects([]);
        } finally {
            setLoading(false);
        }
    };

    const handleSubjectClick = (subject) => {
        setSelectedSubject(subject);
        // Update display text based on selection
        if (subject === "All Subjects") {
            setDisplayText("By Subjects");
        } else {
            setDisplayText(subject);
        }

        if (onSubjectChange) {
            onSubjectChange(subject === "All Subjects" ? null : subject);
        }
    };

    return (
        <div>
            <Menu>
                <MenuButton className="inline-flex items-center gap-2 rounded-md py-1.5 px-3 text-sm/6 font-semibold focus:outline-none data-[open]:rounded-full data-[open]:bg-[#F2F3FF] data-[open]:text-darkblue data-[focus]:outline-1">
                    <span className="regular-text">
                        {loading ? "Loading..." : displayText}
                    </span>
                </MenuButton>

                <MenuItems
                    transition
                    anchor="bottom end"
                    className="w-60 z-20 origin-top-right rounded-xl border border-white/5 bg-white shadow-md p-1 text-sm/6 text-black transition duration-100 ease-out [--anchor-gap:var(--spacing-1)] focus:outline-none data-[closed]:scale-95 data-[hover]:bg-lightgray data-[closed]:opacity-0"
                >
                    <MenuItem>
                        <button
                            onClick={() => handleSubjectClick("All Subjects")}
                            className={`${selectedSubject === "All Subjects" && "bg-[#F0F1FF] text-darkblue"
                                } group flex justify-between w-full items-center gap-2 rounded-lg py-1.5 px-3 data-[focus]:bg-[#F7F7F7]`}
                        >
                            All Subjects
                            {selectedSubject === "All Subjects" && <IoMdCheckmark className="text-darkblue" />}
                        </button>
                    </MenuItem>

                    {subjects.length > 0 ? (
                        subjects.map((subject, index) => (
                            <MenuItem key={index}>
                                <button
                                    onClick={() => handleSubjectClick(subject)}
                                    className={`${selectedSubject === subject && "bg-[#F0F1FF] text-darkblue"
                                        } group flex justify-between w-full items-center gap-2 rounded-lg py-1.5 px-3 data-[focus]:bg-[#F7F7F7]`}
                                >
                                    {subject}
                                    {selectedSubject === subject && <IoMdCheckmark className="text-darkblue" />}
                                </button>
                            </MenuItem>
                        ))
                    ) : (
                        <MenuItem>
                            <div className="px-3 py-1.5 text-gray-500 text-sm">
                                {loading ? "Loading subjects..." : "No subjects available"}
                            </div>
                        </MenuItem>
                    )}

                    {subjects.length > 0 && <div className="my-1 h-px bg-white/5" />}
                </MenuItems>
            </Menu>
        </div>
    );
}

